import { useState, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { ContainerClient } from "@azure/storage-blob";
import apiService from "~/services/service";
import Config from "~/config/env";
import { BulkImportRoutes } from "~/api-routes-v2";
import { getSasToken } from "~/utils/fileUtils";
import { validatePayslipCsv } from "./validation/csvValidator";
import type { ValidationError } from "./validation/types";

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024; // 25MB
const BULK_IMPORT_CONTAINER = "bulk-imports";

/** In local dev, use Vite proxy to avoid CORS (Azure Storage may not allow localhost). */
const getBlobBaseUrl = (): string => {
  if (typeof window === "undefined") {
    return `https://${Config.BLOB_STORAGE_ACCOUNT}.blob.core.windows.net`;
  }
  const isLocal =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  if (isLocal) {
    return `${window.location.origin}/api/blob-proxy`;
  }
  return `https://${Config.BLOB_STORAGE_ACCOUNT}.blob.core.windows.net`;
};

export type ValidationStatus = "idle" | "validating" | "valid" | "invalid";

export type ImportFileState = {
  file: File | null;
  /** Basic file check error (missing, wrong type, size) */
  error: string | null;
};

export const validateImportFile = (file: File | null): string | null => {
  if (!file) {
    return "Please select a CSV file to import.";
  }

  if (!file.name.toLowerCase().endsWith(".csv")) {
    return "Invalid CSV format. Please use the valid template.";
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    return "File must be a CSV under 25MB.";
  }

  return null;
};

const LOG_PREFIX = "[ImportPayslipEvents]";

/**
 * Sanitize file name for blob storage: keep only A–Z, a–z, 0–9.
 * Ensures a unique, cache-busting blob name when combined with Date.now().
 */
function sanitizeFileNameForBlob(fileName: string): string {
  const base = fileName.replace(/\.csv$/i, "").trim();
  const alphanumericOnly = base.replace(/[^A-Za-z0-9]/g, "");
  return alphanumericOnly || "import";
}

const uploadToBlobStorage = async (file: File): Promise<string> => {
  const sasToken = await getSasToken(BULK_IMPORT_CONTAINER, {
    forceRefresh: true,
  });
  console.log(
    `${LOG_PREFIX} SAS token received for container: ${BULK_IMPORT_CONTAINER}`,
  );

  const sanitizedStem = sanitizeFileNameForBlob(file.name);
  const blobName = `payslip-import-${Date.now()}-${sanitizedStem}.csv`;
  const baseUrl = getBlobBaseUrl();
  const containerUrl = `${baseUrl}/${BULK_IMPORT_CONTAINER}?${sasToken}`;

  const containerClient = new ContainerClient(containerUrl);
  const blockBlobClient = containerClient.getBlockBlobClient(blobName);

  console.log(
    `${LOG_PREFIX} File sent to server (uploading to blob): ${file.name}`,
  );
  await blockBlobClient.uploadData(file, {
    blobHTTPHeaders: {
      blobContentType: file.type || "text/csv",
    },
  });

  const storageAccount = Config.BLOB_STORAGE_ACCOUNT;
  const realBlobUrl = `https://${storageAccount}.blob.core.windows.net/${BULK_IMPORT_CONTAINER}/${blobName}?${sasToken}`;
  return realBlobUrl;
};

type ImportMutationResponse = {
  success: boolean;
  message?: string;
  data?: {
    importId: string;
    status: string;
    note?: string;
  };
};

export const useImportPayslipEvents = () => {
  const [fileState, setFileState] = useState<ImportFileState>({
    file: null,
    error: null,
  });

  const [validationStatus, setValidationStatus] =
    useState<ValidationStatus>("idle");
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  );
  const [parsedRowCount, setParsedRowCount] = useState<number>(0);

  /** Unique key for current file; increment on each drop to avoid cache reuse. */
  const [fileKey, setFileKey] = useState<number>(0);

  const runValidation = useCallback(async (file: File): Promise<void> => {
    setValidationStatus("validating");
    setValidationErrors([]);
    setParsedRowCount(0);

    const result = await validatePayslipCsv(file);
    setParsedRowCount(result.rowCount);
    setValidationErrors(result.errors);
    setValidationStatus(result.status === "valid" ? "valid" : "invalid");
  }, []);

  const setFile = useCallback(
    (file: File | null) => {
      setFileState((prev) => ({
        ...prev,
        file,
        error: validateImportFile(file),
      }));
      setValidationStatus("idle");
      setValidationErrors([]);
      setParsedRowCount(0);
      if (file) {
        setFileKey((k) => k + 1);
        runValidation(file);
      }
    },
    [runValidation],
  );

  const clearFile = useCallback(() => {
    setFileState({ file: null, error: null });
    setValidationStatus("idle");
    setValidationErrors([]);
    setParsedRowCount(0);
    setFileKey((k) => k + 1);
  }, []);

  const resetValidation = useCallback(() => {
    setValidationStatus("idle");
    setValidationErrors([]);
    setParsedRowCount(0);
  }, []);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!fileState.file) {
        throw new Error("No file selected.");
      }

      if (validationStatus !== "valid") {
        throw new Error("File must pass validation before import.");
      }

      const blobUrl = await uploadToBlobStorage(fileState.file);

      const response = await apiService<ImportMutationResponse>(
        BulkImportRoutes.importPayrollPlusTasks,
        "POST",
        {
          body: { blobUrl },
          skipCache: true,
        },
      );

      console.log(
        `${LOG_PREFIX} Server accepted:`,
        response?.data?.importId != null
          ? `importId=${response.data.importId}`
          : "OK",
        response?.data?.note ?? response?.message ?? "",
      );
      return response;
    },
  });

  return {
    fileState,
    setFileState,
    setFile,
    clearFile,
    resetValidation,
    fileKey,
    validationStatus,
    validationErrors,
    parsedRowCount,
    importMutation: mutation,
  };
};
